/*
  WHICH PAGES ARE THE RENTERS' (MTR #509; project doc 113).

  The pages a renter or the outside world opens. Each shape is the one its own page already
  parses — imported here, not written a second time — so a page cannot be renamed on one side only.
*/
import { tokenFromPath } from '../quote/publicLink.js'
import { slugFromPath } from '../listing/enquiryLink.js'
import { stayKeyFromPath } from '../listing/publicHome.js'
import { chatTokenFromPath } from '../chat/chatPath.js'
import { tourCodeFromPath } from '../links/shortLink.js'
import { mediaCodeFromPath } from '../links/mediaLink.js'
import { leaseTokenFromPath } from '../lease/leasePath.js'
import { cleanTokenFromPath, moveInTokenFromPath } from '../property/cleanPath.js'
import { STAFF_BASE, GO_BASE, RENTER_BASE } from './hosts.js'

const BROWSE = /^\/homes\/?$/

/** True for a quote, an enquiry form, a home, a renter's chat, the search page, a tour a short link opened, the photos we sent her, and her lease. */
function isRenterPath(path) {
  const p = String(path ?? '')
  return Boolean(tokenFromPath(p) || slugFromPath(p) || stayKeyFromPath(p) || chatTokenFromPath(p) || tourCodeFromPath(p) || mediaCodeFromPath(p) || leaseTokenFromPath(p) || cleanTokenFromPath(p) || moveInTokenFromPath(p) || BROWSE.test(p))
}

const GO_HOST = new URL(GO_BASE).host

/**
 * WHAT go.stayoo.com DOES WITH A PAGE THAT IS NOT A RENTER'S (server side, before the app is sent).
 *
 * go shows the outside world only. Its front door is the search page, which is what somebody who
 * types the address wants; anything else — the staff app, a feedback link — belongs on
 * my.stayoo.com and is sent there. Returns the address to go to, or null to serve the page.
 */
export function goHostRedirect({ host, path, search = '' }) {
  if (String(host ?? '').toLowerCase().split(':')[0] !== GO_HOST) return null
  if (isRenterPath(path)) return null
  if (path === '/' || path === '') return `${GO_BASE}/homes${search}`
  return `${STAFF_BASE}${path}${search}`
}

/**
 * WHAT my.stayoo.com DOES WITH A RENTER'S PAGE (in the browser, where we can tell who is looking).
 *
 * A colleague signed in on this device stays on my.stayoo.com: her view of a home is ours. Anybody
 * else on a renter's page is moved to the renters' address — which is how every link already sent
 * from my.stayoo.com, an ambassador's included, keeps working. Nothing moves until the switch is on.
 */
export function renterRedirect({ origin, path, search = '', hash = '', signedIn, renterBase = RENTER_BASE }) {
  if (signedIn || renterBase === STAFF_BASE || origin !== STAFF_BASE) return null
  if (!isRenterPath(path)) return null
  return `${renterBase}${path}${search}${hash}`
}

/**
 * THE PLATFORM'S OWN ADDRESS IS NOT OURS (Yuliana, 2026-10-12: "it's not allowing me to add more team
 * members"). Railway also serves this app at <service>.up.railway.app, and Supabase's sign-in links still
 * land there. On that address the team service refuses the browser (it answers my.stayoo.com and
 * go.stayoo.com only — as it must), so inviting a colleague failed with "the team service did not answer,
 * twice", day after day, while the service was up. Anybody who opens the platform's address is moved to
 * the same page on my.stayoo.com, query and #fragment kept, so a sign-in link carried there still signs
 * them in. They sign in once more on my.stayoo.com; nothing else changes.
 */
export function platformAddressRedirect({ origin, path = '/', search = '', hash = '' }) {
  let host
  try { host = new URL(String(origin ?? '')).hostname } catch { return null }
  if (!host.endsWith('.up.railway.app')) return null
  return `${STAFF_BASE}${path || '/'}${search}${hash}`
}
